import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import {
  DOCUMENT,
  DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
  documentNavLogoSx,
  documentPageCanvasSx,
  pageShellSx,
} from '../../constants/documentTheme';
import { developerPath, mainAppOrigin } from '../../utils/developerHost';
import { API_SECTIONS } from '../../data/developerApiReference';
import { canAccessDeveloperApiTools, isDeveloperApiToolPath } from '../../utils/partnerTierGates';
import { planNameKey } from '../../utils/partnerPlan';

const SIDEBAR_WIDTH = 260;

const NAV = [
  { key: 'overview', path: '' },
  { key: 'docs', path: 'docs' },
  { key: 'sandbox', path: 'sandbox' },
  { key: 'widgets', path: 'widgets' },
  { key: 'keys', path: 'keys', requiresApi: false },
  { key: 'webhooks', path: 'webhooks', requiresApi: true },
  { key: 'usage', path: 'usage', requiresApi: true },
  { key: 'changelog', path: 'changelog' },
];

const navItemSx = {
  mx: 1,
  mb: 0.5,
  borderRadius: 0,
  color: 'rgba(255,255,255,0.72)',
  '&.Mui-selected': {
    bgcolor: 'rgba(255,255,255,0.1)',
    color: DOCUMENT.ivory,
  },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
  '&.Mui-disabled': {
    opacity: 0.42,
    color: 'rgba(255,255,255,0.45)',
  },
};

const DeveloperLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [entitlements, setEntitlements] = useState(null);

  const partnerToken = typeof window !== 'undefined' && localStorage.getItem('partnerToken');
  const canUseApiTools = canAccessDeveloperApiTools(entitlements);
  const planId = entitlements?.tier || entitlements?.id;
  const planLabel = planId
    ? t(planNameKey(planId), { defaultValue: entitlements?.planName || planId })
    : null;

  const refreshEntitlements = useCallback(async () => {
    try {
      const planRes = await api.get('/partners/plan', { usePartnerAuth: true });
      setEntitlements(planRes.data?.entitlements || null);
    } catch {
      setEntitlements(null);
    }
  }, []);

  useEffect(() => {
    if (partnerToken) {
      refreshEntitlements();
    }
  }, [partnerToken, refreshEntitlements]);

  const isActive = (path) => {
    const full = developerPath(path);
    if (path === '') {
      return location.pathname === full || location.pathname === `${full}/`;
    }
    if (path === 'docs') {
      return location.pathname === full || location.pathname === `${full}/`;
    }
    return location.pathname === full || location.pathname.startsWith(`${full}/`);
  };

  const handleLogout = () => {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partner');
    window.location.href = developerPath('login');
  };

  const renderNavItem = (item) => {
    const locked = Boolean(item.requiresApi) && !canUseApiTools;
    const button = (
      <ListItemButton
        key={item.key}
        component={locked ? 'div' : RouterLink}
        to={locked ? undefined : developerPath(item.path)}
        selected={!locked && isActive(item.path)}
        disabled={locked}
        onClick={locked ? undefined : () => setMobileOpen(false)}
        sx={navItemSx}
        aria-disabled={locked}
      >
        <ListItemText
          primary={t(`developerPortal.nav.${item.key}`)}
          primaryTypographyProps={{ fontSize: 14, fontFamily: DOCUMENT.sans }}
        />
        {locked ? (
          <LockOutlinedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', ml: 1 }} />
        ) : null}
      </ListItemButton>
    );

    if (!locked) return button;

    return (
      <Tooltip
        key={item.key}
        title={t('developerPortal.nav.lockedHint')}
        placement="right"
        enterDelay={300}
      >
        <Box component="span" sx={{ display: 'block' }}>{button}</Box>
      </Tooltip>
    );
  };

  const sidebar = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DOCUMENT.nav,
        color: DOCUMENT.ivory,
        borderRight: `1px solid ${DOCUMENT.rule}`,
      }}
    >
      <Box
        component={RouterLink}
        to={developerPath()}
        onClick={() => setMobileOpen(false)}
        sx={{
          px: 2.5,
          py: 2.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          textDecoration: 'none',
          color: DOCUMENT.ivory,
          display: 'block',
        }}
      >
        <Box
          component="img"
          src="/noBgWhite.png"
          alt="Déjà Vu Nomad"
          sx={{
            ...documentNavLogoSx,
            maxWidth: '100%',
            filter: 'none',
          }}
        />
        <Typography
          sx={{
            mt: 1,
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(201,169,97,0.92)',
          }}
        >
          {t('developerPortal.brand')}
        </Typography>
        {planLabel ? (
          <Box
            sx={{
              mt: 1.25,
              display: 'inline-block',
              px: 0.9,
              py: '3px',
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#C9A961',
              border: '1px solid rgba(201,169,97,0.55)',
            }}
          >
            {t('developerPortal.home.currentPlanBadge', { plan: planLabel })}
          </Box>
        ) : null}
      </Box>

      <List sx={{ flex: 1, py: 1.5, overflowY: 'auto' }}>
        {NAV.map(renderNavItem)}

        <Typography
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1,
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.45)',
          }}
        >
          {t('developerPortal.nav.apiReference')}
        </Typography>
        <ListItemButton
          component={RouterLink}
          to={developerPath('docs/api')}
          selected={location.pathname.includes('/docs/api') && !location.pathname.match(/\/docs\/api\/[^/]+/)}
          onClick={() => setMobileOpen(false)}
          sx={navItemSx}
        >
          <ListItemText
            primary={t('developerPortal.nav.allEndpoints')}
            primaryTypographyProps={{ fontSize: 13.5 }}
          />
        </ListItemButton>
        {API_SECTIONS.map((section) => {
          const sectionPath = `docs/api/${section.id}`;
          const selected = location.pathname.includes(`/docs/api/${section.id}`);
          return (
            <ListItemButton
              key={section.id}
              component={RouterLink}
              to={developerPath(`${sectionPath}/${section.endpoints[0]}`)}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{ ...navItemSx, mb: 0.25, pl: 3, color: 'rgba(255,255,255,0.65)' }}
            >
              <ListItemText
                primary={t(section.titleKey)}
                primaryTypographyProps={{ fontSize: 13 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {partnerToken ? (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            component="a"
            href={`${mainAppOrigin()}/partner/dashboard`}
            sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'none', justifyContent: 'flex-start', px: 1 }}
          >
            {t('developerPortal.nav.partnerPortal')}
          </Button>
          <Button
            onClick={handleLogout}
            sx={{ color: 'rgba(255,255,255,0.65)', textTransform: 'none', justifyContent: 'flex-start', px: 1 }}
          >
            {t('developerPortal.nav.logout')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ ...documentPageCanvasSx, minHeight: '100vh', bgcolor: DOCUMENT.canvas }}>
      {isMobile ? (
        <>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              bgcolor: DOCUMENT.nav,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: DOCUMENT.ivory }}>
              <MenuIcon />
            </IconButton>
            <Box
              component={RouterLink}
              to={developerPath()}
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', minWidth: 0, flex: 1 }}
            >
              <Box
                component="img"
                src="/noBgWhite.png"
                alt="Déjà Vu Nomad"
                sx={{
                  ...documentNavLogoSx,
                  height: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
                  maxWidth: 148,
                  filter: 'none',
                }}
              />
            </Box>
            {planLabel ? (
              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#C9A961',
                  whiteSpace: 'nowrap',
                  mr: 0.5,
                }}
              >
                {planLabel}
              </Typography>
            ) : null}
          </Box>
          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, bgcolor: DOCUMENT.nav } }}
          >
            {sidebar}
          </Drawer>
        </>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: SIDEBAR_WIDTH,
            zIndex: 12,
          }}
        >
          {sidebar}
        </Box>
      )}

      <Box
        component="main"
        sx={{
          width: '100%',
          py: { xs: 3, md: 4 },
        }}
      >
        {/*
          Same xl shell as consumer (My Trips / Packages). Sidebar is fixed;
          left padding clears it without shrinking the overall page width.
        */}
        <Box
          sx={{
            ...pageShellSx,
            // Keep consumer xl width; clear the fixed sidebar on desktop.
            pr: { xs: 2, sm: 3, md: 5 },
            pl: { xs: 2, sm: 3, md: `${SIDEBAR_WIDTH + 32}px` },
          }}
        >
          <Outlet
            context={{
              entitlements,
              setEntitlements,
              refreshEntitlements,
              canUseApiTools,
              isDeveloperApiToolPath,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DeveloperLayout;
